import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

import { GlobalState } from "../../../../context/GlobalContext";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import { grey } from "@mui/material/colors";

import MrpGrid from "../MrpGrid/MrpGrid";
import { API, round1dp } from "../../data";
import { buildSubcategoryColumns } from "../gridColumns";
import { bpGridSx } from "../storeBpTheme";
import parseErrMsgFromError from "utils/errorFunctions/parseErrorMessageFromError";
import "../../businessPlanV2.css";

const SubcategoryView = ({ submissionId, category, division, canEdit, onDataChanged, embedded }) => {
	const { setSnackBar, snackBar, setSnackBarOpen } = GlobalState();
	const [subcategories, setSubcategories] = useState([]);
	const [selectedSubcat, setSelectedSubcat] = useState(null);
	const [showMrpGrid, setShowMrpGrid] = useState(false);
	const [loading, setLoading] = useState(false);

	const fetchSubcategories = async () => {
		if (!submissionId || !category) return;
		try {
			setLoading(true);
			const params = new URLSearchParams({
				submission_id: submissionId,
				category,
			});
			if (division) params.set("division", division);
			const { data } = await axios.get(`${API.SUBCATEGORY_VIEW}?${params.toString()}`);
			if (data.status === "success") {
				setSubcategories(
					data.result.map((sc, i) => ({
						...sc,
						id: `${sc.category}-${sc.sub_category}-${i}`,
					}))
				);
			}
		} catch (err) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrMsgFromError(err), severity: "error" });
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchSubcategories();
		setSelectedSubcat(null);
		setShowMrpGrid(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [submissionId, category]);

	const { ltlTotalQty, newTotalQty, rows } = useMemo(() => {
		const ltlTotalQty = subcategories.reduce((s, r) => s + (r.ltl_qty || 0), 0);
		const newTotalQty = subcategories.reduce((s, r) => s + (r.new_qty || 0), 0);

		if (!subcategories.length) return { ltlTotalQty, newTotalQty, rows: [] };

		const ltlNsv = subcategories.reduce((s, r) => s + (r.ltl_nsv || 0), 0);
		const newNsv = subcategories.reduce((s, r) => s + (r.new_nsv || 0), 0);
		const ltlDispatch = subcategories.reduce((s, r) => s + (r.ltl_inventory_qty || 0), 0);
		const ltlCombineSale = subcategories.reduce(
			(s, r) => s + ((r.ltl_st || 0) / 100) * (r.ltl_inventory_qty || 0),
			0
		);

		const totalRow = {
			id: "TOTAL",
			isTotal: true,
			sub_category: "TOTAL",
			ltl_qty: ltlTotalQty,
			new_qty: newTotalQty,
			ltl_dis: round1dp(
				subcategories.length ? subcategories.reduce((s, r) => s + (r.ltl_dis || 0), 0) / subcategories.length : 0
			),
			new_dis: round1dp(
				subcategories.length ? subcategories.reduce((s, r) => s + (r.new_dis || 0), 0) / subcategories.length : 0
			),
			ltl_opt: subcategories.reduce((s, r) => s + (r.ltl_opt || 0), 0),
			new_opt: subcategories.reduce((s, r) => s + (r.new_opt || 0), 0),
			ltl_st: ltlDispatch ? round1dp((ltlCombineSale / ltlDispatch) * 100) : 0,
			ltl_inventory_qty: ltlDispatch,
			ltl_asp: round1dp(ltlTotalQty ? ltlNsv / ltlTotalQty : 0),
			new_asp: round1dp(newTotalQty ? newNsv / newTotalQty : 0),
			ltl_nsv: round1dp(ltlNsv),
			new_nsv: round1dp(newNsv),
		};

		return { ltlTotalQty, newTotalQty, rows: [totalRow, ...subcategories] };
	}, [subcategories]);

	const columns = useMemo(() => buildSubcategoryColumns(ltlTotalQty, newTotalQty), [ltlTotalQty, newTotalQty]);

	const handleRowClick = ({ row }) => {
		if (row.isTotal) return;
		setSelectedSubcat(row);
		setShowMrpGrid(true);
	};

	const getRowClassName = ({ row }) => {
		if (row.isTotal) return "";
		if (row.completed) return row.total_count === 0 ? "yellow" : "green";
		if (row.total_count === 0 && row.ltl_qty === 0) return "yellow";
		return "";
	};

	return (
		<Box>
			{!embedded && (
				<Typography
					sx={{
						fontSize: 13,
						fontWeight: 700,
						color: grey[700],
						textTransform: "uppercase",
						letterSpacing: "0.05em",
						mb: 1,
					}}>
					{category} — Subcategories
				</Typography>
			)}
			<Box display="flex">
				<DataGrid
					rows={rows}
					columns={columns}
					columnHeaderHeight={35}
					rowHeight={36}
					showCellVerticalBorder
					showColumnVerticalBorder
					getRowClassName={getRowClassName}
					onRowClick={handleRowClick}
					hideFooter
					disableRowSelectionOnClick
					sx={{ ...bpGridSx, minHeight: 280, maxHeight: 480 }}
				/>
			</Box>

			<Dialog open={loading}>
				<Box width="200px" height="100px" display="flex" alignItems="center" justifyContent="center">
					<CircularProgress />
				</Box>
			</Dialog>

			<MrpGrid
				open={showMrpGrid}
				onClose={() => setShowMrpGrid(false)}
				submissionId={submissionId}
				mrpRows={selectedSubcat?.mrpRows || []}
				subCategory={selectedSubcat?.sub_category}
				category={selectedSubcat?.category || category}
				division={selectedSubcat?.division || division}
				canEdit={canEdit}
				onSaved={() => {
					fetchSubcategories();
					onDataChanged?.();
				}}
			/>
		</Box>
	);
};

export default SubcategoryView;
